import { Introductions } from '@platform/client';
import { Input, InputDropdown, Modal } from '@platform/components';
import { useQueryClient } from '@tanstack/react-query';
import { useContext, useState } from 'react';
import { DirectoryPageContext } from './DirectoryPageContext';
import { ProjectAvailableForIntro, SimplifiedUserRole } from '@platform/client/schemas';

const INPUT_LENGTH_ERROR = 'The message should be between 50 and 1000 characters long';
const PRO_SENDING_PROMPT =
  'For example: Hello! I really resonate with your mission to make the fashion sector more sustainable, and would love to bring my 10+ years of marketing experience to help you improve the way you reach your target customers. I’m available Monday April 18 between 3-5 PM ET, Tuesday April 20 between 1-4 PM ET, and April 22 between 2-3 PM ET. Hope we can connect and see if we are a good fit!';
const REP_SENDING_PROMPT =
  'For example: Hello! I’m impressed by your marketing experience, and think you’d be a great fit for our project. We’re on a mission to make the fashion sector more sustainable, and could use your help improving the way we reach our target audience. I’m available Monday April 18 between 3-5 PM ET, Tuesday April 20 between 1-4 PM ET, and April 22 between 2-3 PM ET. Hope we can connect and see if we are a good fit!';

/** A modal for drafting a new introduction to another user, and sending it upon clicking the primary CTA */
const SendIntroModal = ({
  isOpen,
  setIsOpen,
  presetProjectId: projectId,
  projectsAvailableForIntro,
  onMessageSent,
  requireProjectSelection,
}: {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  // A pre-defined project ID, for contexts where this intro modal is specific to one project.
  // This should only be set in cases where requireProjectSelection = false and you're not expecting to
  // show a list of projects.
  presetProjectId?: number;
  projectsAvailableForIntro?: ProjectAvailableForIntro[];
  onMessageSent: () => void;
  requireProjectSelection?: boolean;
}) => {
  const { sendingFromRole, targetUserId, queryKey } = useContext(DirectoryPageContext);

  const [inputError, setInputError] = useState(INPUT_LENGTH_ERROR);
  // -1 is the "Propose a custom project" option
  const [selectedProject, setSelectedProject] = useState<string>(projectId?.toString() ?? '-1');
  const [introText, setIntroText] = useState('');

  const handleTextInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setIntroText(e.target.value);

    if (e.target.value.length < 50 || e.target.value.length > 1000) {
      setInputError(INPUT_LENGTH_ERROR);
    } else {
      setInputError('');
    }
  };

  const queryClient = useQueryClient();
  const sendIntro = Introductions.useSendIntroduction({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [...queryKey],
        });
        setIsOpen(false);
        // showRequestSentModal();
      },
      onError: (_) =>
        setInputError('Something went wrong. Please try again later or contact support.'),
    },
  });

  const sendMessage = async () => {
    sendIntro.mutate({
      data: {
        message: introText,
        projectId: parseInt(selectedProject) === -1 ? null : parseInt(selectedProject),
        fromRole: sendingFromRole,
      },
      targetUserId: targetUserId,
    });

    onMessageSent();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      title={'Request an introduction'}
      headerDivision='divider'
      variant='dialog'
      primaryCTA={{
        text:
          sendingFromRole === SimplifiedUserRole.PROFESSIONAL ?
            'Send Introduction'
          : 'Request an introduction',
        isDisabled: () => inputError.length > 0,
        isLoading: sendIntro.isPending,
        isLoadingText: 'Sending...',
        callback: sendMessage,
      }}
      secondaryCTA={{
        text: 'Cancel',
        callback: () => setIsOpen(false),
      }}
      ignoreExtraPadding
    >
      <div className='flex flex-col gap-4 h-full'>
        {/* If disabled, all intro */}
        {requireProjectSelection && (
          <InputDropdown
            label='Which project would you like to discuss with this Experteer?'
            type='default'
            defaultSelectedKey={projectsAvailableForIntro?.[0]?.projectId.toString() ?? '-1'}
            onSelectionChange={(value) => setSelectedProject(value as string)}
            items={[
              ...(projectsAvailableForIntro ?? []).map((project) => ({
                text: project.projectName,
                textValue: project.projectId.toString(),
              })),
              { text: '✨ Propose a custom project', textValue: '-1' },
            ]}
            inputDisabled
          />
        )}
        <p>Introduce yourself! Some things to include:</p>
        <ul className='ml-4 list-disc flex flex-col'>
          <li>
            <span className='font-bold'>Scheduling:&nbsp;</span>Provide three dates and times you’re
            available within the next week for a 30 minute call to see if you’re a good match
          </li>
          <li>
            <span className='font-bold'>Skills fit:&nbsp;</span>
            {sendingFromRole === SimplifiedUserRole.PROFESSIONAL ?
              'Let them know how your skills can help'
            : 'Let them know why you like their skillset'}
          </li>
          <li>
            <span className='font-bold'>Mission:&nbsp;</span>
            {sendingFromRole === SimplifiedUserRole.PROFESSIONAL ?
              "Let them know why you're interested in working with them"
            : 'Give them a brief summary of what your organization does'}
          </li>
        </ul>
        <div className='h-64 max-h-64'>
          <Input
            type='multiline'
            value={introText}
            onChange={handleTextInput}
            error={introText.length > 0 ? inputError : ''}
            stretchHeight
            placeholder={
              sendingFromRole === SimplifiedUserRole.PROFESSIONAL ?
                PRO_SENDING_PROMPT
              : REP_SENDING_PROMPT
            }
          />
        </div>
      </div>
    </Modal>
  );
};

export default SendIntroModal;
